// Drawing-only pose around the existing common mounting post. No hardware IO.
export const controllerStandPose = Object.freeze({
  pivot: Object.freeze([-5.5, 0, -3]), yaw: Math.PI / 2,
  // Drawing clearance from the existing left glazing; not a fabricated bracket dimension.
  inwardOffset: Object.freeze([.25, 0, 0])
});

export function createControllerStandAssembly(THREE, { sceneRoot, controller, controllerPowerSupply, chaacPowerSupply }) {
  const root = new THREE.Group(); root.name = "controller-and-power-supplies-common-stand";
  root.position.set(...controllerStandPose.pivot);
  root.userData = { schematic: true, role: "shared-controller-psu-mount", yaw: controllerStandPose.yaw };
  sceneRoot.add(root); sceneRoot.updateMatrixWorld(true);
  // The external Chaac lead begins at a stationary sensor. It must not rotate
  // with the power brick; only its final mounting-side section is re-routed.
  sceneRoot.attach(chaacPowerSupply.cable);
  [controller, controllerPowerSupply.root, chaacPowerSupply.root].forEach(object => root.attach(object));
  root.rotation.y = controllerStandPose.yaw;
  root.position.add(new THREE.Vector3(...controllerStandPose.inwardOffset));
  sceneRoot.updateMatrixWorld(true);
  // The complete closed mount moves inward, while clamps remain on the existing
  // post. Extend only the schematic mounting bridges; never move greenhouse metal.
  const fixedFrameBrackets = [], fixedFrameClamps = [];
  const bracketNames = new Set(["controller-psu-left-frame-bracket", "chaac-psu-frame-bracket-lower", "chaac-psu-frame-bracket-upper"]);
  const clampNames = new Set(["controller-psu-frame-clamp", "chaac-psu-left-frame-clamp"]);
  root.traverse(object => {
    if (clampNames.has(object.name)) {
      const at = object.getWorldPosition(new THREE.Vector3());
      at.x = controllerStandPose.pivot[0]; at.z = controllerStandPose.pivot[2];
      object.position.copy(object.parent.worldToLocal(at)); fixedFrameClamps.push(object);
    }
    if (!bracketNames.has(object.name)) return;
    const height = object.geometry.parameters.height;
    const ends = [-1, 1].map(sign => object.localToWorld(new THREE.Vector3(0, sign * height / 2, 0)));
    const pivot = new THREE.Vector3(...controllerStandPose.pivot).add(new THREE.Vector3(...controllerStandPose.inwardOffset));
    const distance = point => Math.hypot(point.x - pivot.x, point.z - pivot.z);
    const postIndex = distance(ends[0]) < distance(ends[1]) ? 0 : 1;
    const postAnchor = ends[postIndex].clone(); postAnchor.x = controllerStandPose.pivot[0]; postAnchor.z = controllerStandPose.pivot[2];
    const supplyAnchor = ends[1 - postIndex].clone();
    const a = object.parent.worldToLocal(supplyAnchor.clone()), b = object.parent.worldToLocal(postAnchor.clone());
    object.position.copy(a).add(b).multiplyScalar(.5);
    object.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize());
    object.scale.y = a.distanceTo(b) / height;
    fixedFrameBrackets.push({ object, postAnchor, supplyAnchor });
  });
  sceneRoot.updateMatrixWorld(true);
  const replacedCableGeometry = chaacPowerSupply.rerouteMountedSupply();
  return { root, replacedCableGeometry, fixedFrameBrackets, fixedFrameClamps, pivot: new THREE.Vector3(...controllerStandPose.pivot), yaw: controllerStandPose.yaw };
}

export function controllerFocusTheta(THREE, boardRoot, side) {
  boardRoot.updateWorldMatrix(true, false);
  const facing = new THREE.Vector3(0, 0, side === "display" ? -1 : 1).transformDirection(boardRoot.matrixWorld);
  return Math.atan2(facing.x, facing.z) + (side === "display" ? 0 : .12);
}
